export type PricebookUnitCategory =
  | 'Common'
  | 'Time'
  | 'Weight'
  | 'Volume'
  | 'Distance'
  | 'Packaging';

/**
 * How a unit reads on a CUSTOMER-facing document (estimate, quote, invoice).
 * `label` above is the operator-facing selector wording — it deliberately
 * carries the canonical code ("Hour (HR)") so whoever picks a unit sees the
 * value that gets stored. A customer must never see that; they get short,
 * human words instead, singular/plural.
 *
 * `null` means the unit is GENERIC — "each" — and prints as nothing at all.
 * "1 each" is noise on a customer document: the quantity already says it.
 */
export interface PricebookUnitDisplay {
  /** Wording at a quantity of exactly 1 — "hr", "box". */
  one: string;
  /** Wording at any other quantity, including decimals — "hrs", "boxes". */
  other: string;
}

export interface PricebookUnitOption {
  value: string;
  label: string;
  category: PricebookUnitCategory;
  /** Customer-facing wording; `null` for the generic unit (prints nothing). */
  display: PricebookUnitDisplay | null;
}

export const PRICEBOOK_UNIT_OPTIONS: readonly PricebookUnitOption[] = [
  { value: 'EA', label: 'Each (EA)', category: 'Common', display: null },
  { value: 'SET', label: 'Set', category: 'Common', display: { one: 'set', other: 'sets' } },
  { value: 'JOB', label: 'Job', category: 'Common', display: { one: 'job', other: 'jobs' } },
  {
    value: 'SVC',
    label: 'Service',
    category: 'Common',
    display: { one: 'service', other: 'services' },
  },
  { value: 'HR', label: 'Hour (HR)', category: 'Time', display: { one: 'hr', other: 'hrs' } },
  { value: 'DAY', label: 'Day', category: 'Time', display: { one: 'day', other: 'days' } },
  { value: 'WK', label: 'Week', category: 'Time', display: { one: 'week', other: 'weeks' } },
  { value: 'MO', label: 'Month', category: 'Time', display: { one: 'month', other: 'months' } },
  { value: 'YR', label: 'Year', category: 'Time', display: { one: 'year', other: 'years' } },
  { value: 'LB', label: 'Pound (LB)', category: 'Weight', display: { one: 'lb', other: 'lbs' } },
  { value: 'OZ', label: 'Ounce (OZ)', category: 'Weight', display: { one: 'oz', other: 'oz' } },
  { value: 'KG', label: 'Kilogram (KG)', category: 'Weight', display: { one: 'kg', other: 'kg' } },
  { value: 'TON', label: 'Ton', category: 'Weight', display: { one: 'ton', other: 'tons' } },
  {
    value: 'GAL',
    label: 'Gallon (GAL)',
    category: 'Volume',
    display: { one: 'gal', other: 'gal' },
  },
  { value: 'QT', label: 'Quart (QT)', category: 'Volume', display: { one: 'qt', other: 'qt' } },
  { value: 'L', label: 'Liter (L)', category: 'Volume', display: { one: 'L', other: 'L' } },
  {
    value: 'CUFT',
    label: 'Cubic Foot (CUFT)',
    category: 'Volume',
    display: { one: 'cu ft', other: 'cu ft' },
  },
  { value: 'IN', label: 'Inch (IN)', category: 'Distance', display: { one: 'in', other: 'in' } },
  { value: 'FT', label: 'Foot (FT)', category: 'Distance', display: { one: 'ft', other: 'ft' } },
  { value: 'YD', label: 'Yard (YD)', category: 'Distance', display: { one: 'yd', other: 'yd' } },
  { value: 'M', label: 'Meter (M)', category: 'Distance', display: { one: 'm', other: 'm' } },
  {
    value: 'SQFT',
    label: 'Square Foot (SQFT)',
    category: 'Distance',
    display: { one: 'sq ft', other: 'sq ft' },
  },
  {
    value: 'SQYD',
    label: 'Square Yard (SQYD)',
    category: 'Distance',
    display: { one: 'sq yd', other: 'sq yd' },
  },
  {
    value: 'LF',
    label: 'Linear Foot (LF)',
    category: 'Distance',
    display: { one: 'linear ft', other: 'linear ft' },
  },
  { value: 'BOX', label: 'Box', category: 'Packaging', display: { one: 'box', other: 'boxes' } },
  { value: 'BAG', label: 'Bag', category: 'Packaging', display: { one: 'bag', other: 'bags' } },
  { value: 'ROLL', label: 'Roll', category: 'Packaging', display: { one: 'roll', other: 'rolls' } },
  {
    value: 'BDL',
    label: 'Bundle',
    category: 'Packaging',
    display: { one: 'bundle', other: 'bundles' },
  },
  {
    value: 'PLT',
    label: 'Pallet',
    category: 'Packaging',
    display: { one: 'pallet', other: 'pallets' },
  },
  {
    value: 'CTN',
    label: 'Carton',
    category: 'Packaging',
    display: { one: 'carton', other: 'cartons' },
  },
] as const;

const CATEGORY_ORDER: readonly PricebookUnitCategory[] = [
  'Common',
  'Time',
  'Weight',
  'Volume',
  'Distance',
  'Packaging',
];

export const PRICEBOOK_UNIT_OPTION_GROUPS = CATEGORY_ORDER.map((category) => ({
  category,
  options: PRICEBOOK_UNIT_OPTIONS.filter((option) => option.category === category),
}));

export function findUnitOption(value: string | undefined | null): PricebookUnitOption | undefined {
  if (!value) return undefined;
  return PRICEBOOK_UNIT_OPTIONS.find((option) => option.value === value);
}

/** Relevant unit categories per item type. Types not listed → all categories. */
export const UNIT_CATEGORIES_BY_TYPE: Partial<Record<string, readonly PricebookUnitCategory[]>> = {
  service: ['Common', 'Time'],
  material: ['Common', 'Weight', 'Volume', 'Distance', 'Packaging'],
  equipment: ['Common', 'Packaging'],
};

export function getUnitOptionGroupsForType(type: string, showAll = false) {
  const cats = UNIT_CATEGORIES_BY_TYPE[type];
  if (showAll || !cats) return PRICEBOOK_UNIT_OPTION_GROUPS;
  return PRICEBOOK_UNIT_OPTION_GROUPS.filter((g) => cats.includes(g.category));
}
